import { ref, computed, watch } from 'vue'
import apiService from '../services/apiService'
import { makeBreadcrumbItems, getParentPath } from '../utils/dateHelpers'

export function useFolderNavigation(toast) {
  const currentPath = ref('/')
  const loading = ref(false)
  const folders = ref([])
  const images = ref([])
  const searchQuery = ref('')
  const imageLoadingStates = ref({})

  // Lazy server pagination (limit + cursor)
  const PAGE_SIZE = 400
  const nextCursor = ref(null)
  const reachedEnd = ref(false)
  const fetchingMore = ref(false)
  const pagesCache = ref([])
  const totalItems = ref(0)

  // Memory management
  const MAX_CACHED_OBJECTS = 20000
  const CLEANUP_THRESHOLD = 18000

  // Request cancellation
  let currentAbortController = null
  let requestVersion = 0

  // Load stats
  const lastLoadStats = ref({
    source: 's3',
    durationMs: 0,
    returned: 0,
    limit: 0,
    total: 0
  })

  // Retry state
  const retryState = ref({
    count: 0,
    maxRetries: 3,
    baseDelay: 1000,
    lastError: null,
    showBanner: false
  })

  const aggregatedImages = computed(() => pagesCache.value.flat())

  // Breadcrumbs
  const breadcrumbItems = computed(() => makeBreadcrumbItems(currentPath.value))

  // Filtered data
  const filteredFolders = computed(() => {
    if (!searchQuery.value) return folders.value
    return folders.value.filter(folder =>
      folder.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  })

  const filteredImages = computed(() => {
    if (!searchQuery.value) return images.value
    return images.value.filter(image =>
      image.name.toLowerCase().includes(searchQuery.value.toLowerCase())
    )
  })

  // Load data (reset=true on path change / refresh)
  const loadData = async (reset = true) => {
    if (currentAbortController) {
      currentAbortController.abort()
    }

    currentAbortController = new AbortController()
    const currentVersion = ++requestVersion

    if (reset) {
      loading.value = true
      nextCursor.value = null
      reachedEnd.value = false
      pagesCache.value = []
      images.value = []
      totalItems.value = 0
    } else {
      fetchingMore.value = true
    }

    try {
      const response = await apiService.getImagesAndFolders(
        currentPath.value,
        {
          limit: PAGE_SIZE,
          cursor: reset ? undefined : nextCursor.value || undefined,
          query: searchQuery.value?.trim() || undefined
        },
        currentAbortController.signal
      )

      if (currentVersion !== requestVersion) return

      if (reset) {
        folders.value = response.folders || []
      } else if (response.folders) {
        folders.value = response.folders
      }

      const batch = response.images || []
      pagesCache.value.push(batch)
      images.value = aggregatedImages.value

      nextCursor.value = response.nextCursor || null
      const hasMore = response.hasMore ?? Boolean(nextCursor.value)
      reachedEnd.value = !hasMore
      totalItems.value = response.totalItems ?? response.total ?? response.stats?.total ?? images.value.length

      if (response.stats) {
        lastLoadStats.value = {
          source: response.stats.source || 'metadata',
          durationMs: response.stats.durationMs ?? response.stats.duration_ms ?? 0,
          returned: response.stats.returned ?? batch.length,
          limit: response.stats.limit ?? PAGE_SIZE,
          total: response.stats.total ?? totalItems.value
        }
      }

      if (batch.length > 0) {
        batch.forEach(img => { if (img?.id) imageLoadingStates.value[img.id] = true })
      }

      // Memory management
      if (images.value.length > CLEANUP_THRESHOLD) {
        const excessObjects = images.value.length - MAX_CACHED_OBJECTS
        if (excessObjects > 0) {
          let removedCount = 0
          while (pagesCache.value.length > 1 && removedCount < excessObjects) {
            const removedBatch = pagesCache.value.shift()
            removedCount += removedBatch.length
          }
          images.value = aggregatedImages.value
          toast.add({
            severity: 'info',
            summary: 'Управление памятью',
            detail: `Освобождено место: удалено ${removedCount} старых элементов из кэша`,
            life: 3000
          })
        }
      }

      retryState.value.count = 0
      retryState.value.showBanner = false

    } catch (error) {
      if (error.name === 'AbortError') return

      console.error('Ошибка загрузки данных:', error)
      retryState.value.lastError = error.message || String(error)

      if (!reset && retryState.value.count < retryState.value.maxRetries) {
        retryState.value.count++
        const delay = retryState.value.baseDelay * Math.pow(2, retryState.value.count - 1)
        retryState.value.showBanner = true
        setTimeout(() => {
          if (retryState.value.count > 0) loadData(false)
        }, delay)
      } else {
        toast.add({
          severity: 'error',
          summary: 'Ошибка загрузки',
          detail: reset ? 'Не удалось загрузить данные' : 'Не удалось дозагрузить данные',
          life: 5000
        })

        if (reset) {
          folders.value = []
          images.value = []
          imageLoadingStates.value = {}
        } else {
          retryState.value.showBanner = true
        }
      }
    } finally {
      if (reset) loading.value = false
      else fetchingMore.value = false

      if (currentVersion === requestVersion) {
        currentAbortController = null
      }
    }
  }

  const fetchNextPage = async () => {
    if (fetchingMore.value || loading.value) return
    if (reachedEnd.value) return
    await loadData(false)
  }

  const refreshData = () => {
    loadData(true)
  }

  const navigateBreadcrumb = (item) => {
    if (item?.path != null) {
      currentPath.value = item.path
      loadData()
    }
  }

  const openFolder = (folder) => {
    currentPath.value = folder.path
    refreshData()
  }

  const goBack = () => {
    currentPath.value = getParentPath(currentPath.value)
    refreshData()
  }

  const manualRetry = async () => {
    retryState.value.showBanner = false
    retryState.value.count = 0
    retryState.value.lastError = ''
    await loadData(true)
  }

  const handleImageError = (event) => {
    const id = event.target.getAttribute('data-image-id')
    if (id && imageLoadingStates.value[id]) {
      delete imageLoadingStates.value[id]
    }
    event.target.src = '/placeholder-image.png'
  }

  const handleImageLoad = (event) => {
    const id = event.target.getAttribute('data-image-id')
    if (id && imageLoadingStates.value[id]) {
      delete imageLoadingStates.value[id]
    }
  }

  // Debounced search
  let searchTimeout = null
  watch(searchQuery, (newQuery, oldQuery) => {
    if (newQuery !== oldQuery) {
      clearTimeout(searchTimeout)
      searchTimeout = setTimeout(() => {
        refreshData()
      }, 500)
    }
  })

  // Clear image loading states on path change
  watch(currentPath, () => {
    imageLoadingStates.value = {}
  })

  return {
    currentPath, loading, folders, images, breadcrumbItems,
    searchQuery, filteredFolders, filteredImages,
    reachedEnd, fetchingMore, totalItems,
    retryState, imageLoadingStates,
    loadData, fetchNextPage, openFolder, goBack,
    navigateBreadcrumb, refreshData, manualRetry,
    handleImageError, handleImageLoad
  }
}
